import { FetchError, NetworkFetchError } from '@yandex-360/telemost-sdk';
import _pick from 'lodash/pick';
import { matchPath } from 'react-router';
import { batchActions } from 'redux-batched-actions';
import { actionChannel, all, call, delay, fork, put, select, take, takeEvery } from 'typed-redux-saga';

import { ErrorPageType } from '../../../../../shared/components/error-page/types';
import { resetErrorPage, setErrorPageAction } from '../../../../../shared/features/config';
import { selectErrorPageType, selectTelemostHost } from '../../../../../shared/features/config/selectors';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { cloudApi } from '../../../../../shared/helpers/transport';
import { createVHLiveEventsChannel, fetchStreamInfo } from '../../../../../shared/helpers/vh-live-events';
import { BroadcastStatus } from '../../../../../types';
import {
    getBroadcastLiveData,
    getBroadcastViewStatus,
    selectBroadcastEndedAt,
    selectIsUgcLiveBroadcast,
    selectWasBroadcastPlayerViewingEnded,
} from './../selectors';
import { strmPlayerViewingEndedAction, updateViewerState, vhLivePostMessageEvent } from './../slice';

const logger = createLogger('broadcast-viewer-sagas');

const STRM_BROADCAST_STATE_MAX_ATTEMPTS = 3;

/**
 * @deprecated
 */
function* watchUgcLiveStatusSaga() {
    while (true) {
        const { streamUrlJson } = yield* select(getBroadcastLiveData);
        const broadcastUri = yield* call(getBroadcastLiveUri);

        if (!streamUrlJson) {
            return;
        }

        const vhLivePostMessageEventChannel = yield* actionChannel(vhLivePostMessageEvent);

        while (true) {
            const data = yield* call(fetchStreamInfo, streamUrlJson);

            if (data.ugc_live_status === 'onair') {
                const vhEventsChannel = yield* call(createVHLiveEventsChannel, { streamUrl: streamUrlJson });

                while (true) {
                    const vhData = yield* take(vhEventsChannel);

                    if (vhData.viewers !== undefined) {
                        yield* put(updateViewerState({ viewers: vhData.viewers }));
                    }

                    if (vhData.ugc_live_status !== 'onair') {
                        const state = yield* call([cloudApi, cloudApi.connectToBroadcast], broadcastUri);
                        vhEventsChannel?.close();
                        yield* put(
                            updateViewerState({
                                ..._pick(state, ['stream_uri', 'status']),
                                ugc_live_status: vhData.ugc_live_status,
                                started_at: undefined,
                            })
                        );
                        break;
                    }
                }
            } else {
                const {
                    payload: { event, liveStatus },
                } = yield* take(vhLivePostMessageEventChannel);
                if (event === 'contentStatusChanged' && liveStatus === 'onair') {
                    const { start_time } = yield* call(fetchStreamInfo, streamUrlJson);

                    yield* put(
                        updateViewerState({
                            status: BroadcastStatus.STARTED,
                            ugc_live_status: liveStatus,
                            ...(start_time ? { started_at: Number(start_time) } : {}),
                        })
                    );
                    break;
                }
            }
        }
    }
}

/**
 * @description обрабатывает ошибку поллинга,
 * @returns {boolean} - возвращает true, если поллинг продолжать можно, false - если нужно остановить
 */
function* handlePollingError(error: unknown, attempts: number) {
    if (!FetchError.is(error)) {
        logger.error(error, {
            method: 'watchStrmBroadcastState',
        });
        yield* put(
            setErrorPageAction({
                type: ErrorPageType.DEFAULT,
                reason: {
                    method: 'watchStrmBroadcastStateSaga',
                },
            })
        );
        return false;
    }

    if (error.status >= 500) {
        attempts++;
        if (attempts >= STRM_BROADCAST_STATE_MAX_ATTEMPTS) {
            logger.error(error, {
                method: 'watchStrmBroadcastStateSaga',
                additional: {
                    kind: 'max_attempts_number_reached',
                    value: attempts,
                },
            });
            yield* put(
                setErrorPageAction({
                    type: ErrorPageType.DEFAULT,
                    reason: {
                        method: 'watchStrmBroadcastStateSaga',
                        code: error.status,
                        type: 'max_attempts_number_reached',
                    },
                })
            );
            return false;
        }
        return true;
    }

    logger.error(error, {
        method: 'watchStrmBroadcastState',
    });
    switch (error.status) {
        case 403:
            yield* put(
                setErrorPageAction({
                    type: ErrorPageType.BROADCAST_ORGANIZATION_MISMATCHES,
                })
            );
            break;
        case 404:
            yield* put(
                setErrorPageAction({
                    type: ErrorPageType.BROADCAST_NOT_FOUND,
                })
            );
            break;
        default:
            yield* put(
                setErrorPageAction({
                    type: ErrorPageType.DEFAULT,
                    reason: {
                        method: 'watchStrmBroadcastStateSaga',
                        code: error.status,
                    },
                })
            );
            break;
    }
    return false;
}

function* watchStrmBroadcastStateSaga() {
    const broadcastUri = yield* call(getBroadcastLiveUri);
    let attempts = 0;
    while (true) {
        try {
            const { refresh_interval_ms, started_at, player_url, ...broadcastState } = yield* call(
                [cloudApi, cloudApi.getBroadcastState],
                broadcastUri
            );
            const updatedStartedAt = started_at ? new Date(started_at).getTime() / 1000 : null;
            if (attempts) {
                attempts = 0;
            }
            yield* put(
                updateViewerState({
                    ...broadcastState,
                    ...(updatedStartedAt && !isNaN(updatedStartedAt) ? { started_at: updatedStartedAt } : {}),
                    ...(player_url ? { player_url } : {}),
                })
            );
            if (!refresh_interval_ms) {
                return;
            }
            yield* delay(refresh_interval_ms);
        } catch (error) {
            if (NetworkFetchError.is(error)) {
                continue;
            }
            const isErrorHandled = yield* call(handlePollingError, error, attempts);
            if (!isErrorHandled) {
                return;
            }
            attempts++;
        }
    }
}

function* getBroadcastLiveUri() {
    const telemostHost = yield* select(selectTelemostHost);
    const match = yield* call(matchPath<{ broadcastId: string }>, location.pathname, {
        path: '/live/:broadcastId',
        exact: true,
        strict: false,
    });
    return `https://${telemostHost}/live/${match?.params.broadcastId}`;
}

function* watchBroadcastStateSaga() {
    const status = yield* select(getBroadcastViewStatus);
    const errorPageType = yield* select(selectErrorPageType);
    if (status === BroadcastStatus.NONE || (errorPageType && errorPageType !== ErrorPageType.BROADCAST_GONE)) {
        return;
    }

    const isUgcLiveBroadcast = yield* select(selectIsUgcLiveBroadcast);
    if (isUgcLiveBroadcast) {
        yield* fork(watchUgcLiveStatusSaga);
        return;
    }
    yield* fork(watchStrmBroadcastStateSaga);
}

function* waitBroadcastPlayerViewingForEnded() {
    let wasPlayerViewingEnded = yield* select(selectWasBroadcastPlayerViewingEnded);
    if (wasPlayerViewingEnded) {
        return;
    }
    while (true) {
        yield* take(updateViewerState);
        wasPlayerViewingEnded = yield* select(selectWasBroadcastPlayerViewingEnded);

        if (wasPlayerViewingEnded) {
            return;
        }
    }
}

function* watchBroadcastStatus() {
    let prevStatus: BroadcastStatus = yield* select(getBroadcastViewStatus);
    const errorPageType = yield* select(selectErrorPageType);
    if (prevStatus === BroadcastStatus.NONE || (errorPageType && errorPageType !== ErrorPageType.BROADCAST_GONE)) {
        return;
    }
    while (true) {
        yield* take(updateViewerState);
        const status = yield* select(getBroadcastViewStatus);

        if (status === BroadcastStatus.ENDED) {
            if (prevStatus === BroadcastStatus.ONAIR) {
                yield* call(waitBroadcastPlayerViewingForEnded);
            }
            const endedAt = yield* select(selectBroadcastEndedAt);
            yield* put(setErrorPageAction({ type: ErrorPageType.BROADCAST_GONE, params: { ended: true, endedAt } }));
        } else if (prevStatus === BroadcastStatus.ENDED) {
            yield* put(batchActions([resetErrorPage(), updateViewerState({ wasPlayerViewingEnded: false })]));
        }

        prevStatus = status;
    }
}

function* handleStrmPlayerViewingEnded() {
    yield* put(updateViewerState({ wasPlayerViewingEnded: true }));
}

/**
 *
 */
export default function* broadcastViewerRootSaga(): Generator {
    yield* all([
        fork(watchBroadcastStatus),
        fork(watchBroadcastStateSaga),
        takeEvery(strmPlayerViewingEndedAction, handleStrmPlayerViewingEnded),
    ]);
}
